import type { ReactNode } from "react";
import type { ConnectionHubError } from "../../../api/connectionHub.types";
import type { ProviderSetupGuide } from "../../../constants/providerSetupGuides.constants";
import ConnectionErrorPanel from "../../common/ConnectionErrorPanel/ConnectionErrorPanel";
import PhaseActions from "../PhaseActions/PhaseActions";
import StepsAccordion from "../StepsAccordion/StepsAccordion";
import styles from "./FilterPhasePanel.module.scss";

export interface FilterPhasePanelProps {
  /** Takes the description's place: what to do next is the panel's own second line. */
  error?: ConnectionHubError | string | null;
  description: string;
  guide?: ProviderSetupGuide;
  forwardingAddress?: string | null;
  settingsUrl: string | null;
  settingsLabel?: string;
  confirmLabel?: string;
  declineLabel?: string;
  /** Named on the "this will open X as …" step before the settings are opened. */
  providerName: string;
  accountEmail: string;
  /** Sends the step sequence back to the start when the inbox moves on. */
  resetKey?: string;
  appearance?: "default" | "primary";
  confirmDismissible?: boolean;
  confirmBeforeOpen?: boolean;
  /** Passed to PhaseActions: the delegate page's Back, on the button row. */
  leading?: ReactNode;
  canOpen?: boolean;
  canCopy?: boolean;
  canConfirm?: boolean;
  disabled?: boolean;
  busy?: boolean;
  onOpen: () => void;
  onConfirm: () => void;
}

const FilterPhasePanel = ({ error, description, guide, forwardingAddress, settingsUrl, settingsLabel = "Open Filter Settings", confirmLabel = "I’ve Created Filter", declineLabel = "I’ve Not Created Filter", providerName, accountEmail, resetKey, appearance = "default", confirmDismissible = false, confirmBeforeOpen = false, leading, canOpen = true, canCopy = false, canConfirm = true, disabled = false, busy = false, onOpen, onConfirm }: FilterPhasePanelProps): JSX.Element => (
  <div className={styles.panel}>
    {error ? <ConnectionErrorPanel error={error} /> : <p className={styles.description}>{description}</p>}
    {guide ? <StepsAccordion steps={guide.filter} {...(guide.notice ? { notice: guide.notice } : {})} videoUrl={guide.filterVideoUrl} phase="filter" providerName={guide.displayName} forwardingAddress={forwardingAddress} /> : null}
    <PhaseActions
      providerName={providerName}
      accountEmail={accountEmail}
      settingsUrl={settingsUrl}
      settingsLabel={settingsLabel}
      confirmLabel={confirmLabel}
      declineLabel={declineLabel}
      appearance={appearance}
      confirmDismissible={confirmDismissible}
      confirmBeforeOpen={confirmBeforeOpen}
      leading={leading}
      canOpen={canOpen}
      canCopy={canCopy}
      canConfirm={canConfirm}
      disabled={disabled}
      busy={busy}
      {...(resetKey ? { resetKey } : {})}
      onOpen={onOpen}
      onConfirm={onConfirm}
    />
  </div>
);

export default FilterPhasePanel;
